import { FeatureFlagEnum } from "#root/config/enums/feature-flag.enum";
import { Permissions } from "#root/shared/enums/permission.enum";

export interface GlobalSearchAccess {
  permissions: Permissions[];
  mode: "all" | "any";
}

export interface GlobalSearchFunctionality {
  label: string;
  route: string;
  access: GlobalSearchAccess | null;
  featureFlag?: FeatureFlagEnum;
  keywords: string[];
}

export interface GlobalSearchScreen {
  title: string;
  route: string;
  icon: string;
  access: GlobalSearchAccess | null;
  featureFlag?: FeatureFlagEnum;
  keywords: string[];
  functionalities: GlobalSearchFunctionality[];
}

const allOf = (...permissions: Permissions[]): GlobalSearchAccess => ({ permissions, mode: "all" });
const anyOf = (...permissions: Permissions[]): GlobalSearchAccess => ({ permissions, mode: "any" });

const configScreen = (
  title: string,
  route: string,
  icon: string,
  screenPermission: Permissions,
  actionLabel: string,
  actionPermission: Permissions,
): GlobalSearchScreen => ({
  title,
  route,
  icon,
  access: allOf(screenPermission),
  keywords: [],
  functionalities: [{ label: actionLabel, route, access: allOf(actionPermission), keywords: [] }],
});

const REPORT_KEYWORDS = ["reporte", "informe", "excel", "pdf", "exportar"];

const REPORTS: { label: string; route: string; permission: Permissions }[] = [
  { label: "Antigüedad de saldos", route: "/crm/report/old-balances", permission: Permissions.CanViewOldBalancesReport },
  { label: "Estado de cuenta de clientes", route: "/crm/report/customers-payment-status", permission: Permissions.CanViewCustomerPaymentStatusReport },
  { label: "Estado de cuenta agrupado", route: "/crm/report/customers-payment-group", permission: Permissions.CanViewCustomerWalletReport },
  { label: "Devoluciones pendientes", route: "/crm/report/pending-with-drawal", permission: Permissions.CanViewPendingWithdrawalReport },
  { label: "Reporte de retiros", route: "/crm/report/withdrawals-report", permission: Permissions.CanViewWithdrawalRequestsApprovedReport },
  { label: "Penalidades por retiro", route: "/crm/report/penalty-withdrawal-report", permission: Permissions.CanViewWithdrawalRequestsApprovedReport },
  { label: "Leads generales", route: "/crm/report/general-leads-report", permission: Permissions.CanViewGeneralLeadsReport },
  { label: "Razones de pérdida", route: "/crm/report/loss-reason-report", permission: Permissions.CanViewOpportunitiesKpis },
  { label: "Ventas internas vs externas", route: "/crm/report/internal-external-sales-report", permission: Permissions.CanViewOpportunitiesKpis },
  { label: "Clientes por asesores", route: "/crm/report/customers-advisor", permission: Permissions.CanViewClientQuoteReport },
  { label: "Estado de cuenta de asesores", route: "/crm/report/advisor-payment-status", permission: Permissions.CanViewAdvisorPaymentStatusReport },
  { label: "Inmuebles más vendidos", route: "/crm/report/top-sold", permission: Permissions.CanViewTopSoldProductReport },
  { label: "Inmuebles más cotizados", route: "/crm/report/top-quote", permission: Permissions.CanViewTopQuoteProductReport },
  { label: "Inmuebles cotizados por cliente", route: "/crm/report/quoted-products-client", permission: Permissions.CanViewClientQuoteReport },
  { label: "Orígenes de oportunidades", route: "/crm/report/opportunity-origins", permission: Permissions.CanViewOpportunityOriginsReport },
  { label: "Clientes por productos cotizados", route: "/crm/report/clients-by-quoted-product", permission: Permissions.CanViewClientByProductReport },
  { label: "Maestro de oportunidades", route: "/crm/report/master", permission: Permissions.CanViewMasterOpportunityReport },
  { label: "Maestro de leads", route: "/crm/report/master-leads", permission: Permissions.CanViewMasterProspectReport },
  { label: "Maestro de clientes", route: "/crm/report/master-client", permission: Permissions.CanViewCustomerInfoReport },
  { label: "Maestro de inmuebles", route: "/crm/report/master-product", permission: Permissions.CanViewProductDatasheetReport },
  { label: "Inmuebles disponibles", route: "/crm/report/available-products", permission: Permissions.CanViewAvailableProductsReport },
  { label: "Historial de pagos", route: "/crm/report/payments-history", permission: Permissions.CanViewPaymentHistoryReport },
  { label: "Pagos de reserva", route: "/crm/report/payments-reserve-history", permission: Permissions.CanViewPaymentReservesHistoryReport },
  { label: "Información de clientes activos", route: "/crm/report/customer-info-report", permission: Permissions.CanViewCustomersHistoryReport },
  { label: "Cuentas por cobrar", route: "/crm/report/accounts-receivable", permission: Permissions.CanViewAccountsReceivableReport },
  { label: "Cumplimiento de documentación", route: "/crm/report/documentation-compliance", permission: Permissions.CanViewDocumentationComplianceReport },
  { label: "Comisiones pagadas por mes", route: "/crm/report/paid-commission-per-month", permission: Permissions.CanViewPaidCommissionPerMonthReport },
  { label: "Comisiones pendientes de pago", route: "/crm/report/pending-paid-commissions", permission: Permissions.CanViewPendingPaymentCommissionReport },
  { label: "Comisiones por proyecto", route: "/crm/report/commissions-per-project", permission: Permissions.CanViewCommissionsPerProjectReport },
  { label: "Comisiones disponibles para pago", route: "/crm/report/commissions-available-for-payment", permission: Permissions.CanViewCommissionsAvailableForPaymentReport },
  { label: "Tiempo de transición entre pasos", route: "/crm/report/opportunity-step-transition-time", permission: Permissions.CanViewOpportunityStepTransitionTimeReport },
];

export const GLOBAL_SEARCH_SCREENS: GlobalSearchScreen[] = [
  {
    title: "Planes de pagos",
    route: "/crm/payment-orders",
    icon: "heroicons_outline:receipt-percent",
    access: allOf(Permissions.CanViewPaymentOrders),
    keywords: ["plan de pago", "cuotas", "financiamiento"],
    functionalities: [],
  },
  {
    title: "Oportunidades",
    route: "/crm/opportunity",
    icon: "heroicons_outline:shopping-cart",
    access: allOf(Permissions.CanViewOpportunities),
    keywords: [],
    functionalities: [
      { label: "Crear oportunidad", route: "/crm/opportunity", access: allOf(Permissions.CanCreateOpportunities), keywords: ["nueva venta", "negocio", "nueva oportunidad"] },
    ],
  },
  {
    title: "Clientes",
    route: "/crm/customers",
    icon: "heroicons_outline:users",
    access: allOf(Permissions.CanViewCustomers),
    keywords: [],
    functionalities: [
      { label: "Crear cliente", route: "/crm/customers/create", access: allOf(Permissions.CanCreateCustomers), keywords: ["nuevo cliente", "alta", "registrar cliente"] },
      {
        label: "Crear clientes desde Excel",
        route: "/crm/customers",
        access: allOf(Permissions.CanCreateCustomers),
        featureFlag: FeatureFlagEnum.MASS_CUSTOMER_CREATION,
        keywords: ["carga masiva", "importar clientes", "subir excel"],
      },
    ],
  },
  {
    title: "Leads",
    route: "/crm/prospects",
    icon: "heroicons_outline:user",
    access: allOf(Permissions.CanViewProspects),
    keywords: [],
    functionalities: [
      { label: "Crear lead", route: "/crm/prospects/create", access: allOf(Permissions.CanCreateProspects), keywords: ["nuevo lead", "prospecto", "registrar lead"] },
      { label: "Importar leads desde Excel", route: "/crm/prospects", access: allOf(Permissions.CanCreateProspects), keywords: ["carga masiva", "subir excel", "importar prospectos"] },
      { label: "Descargar plantilla de leads", route: "/crm/prospects", access: allOf(Permissions.CanCreateProspects), keywords: ["plantilla excel", "formato importacion"] },
    ],
  },
  {
    title: "Inmuebles",
    route: "/inventory/products",
    icon: "heroicons_outline:shopping-bag",
    access: allOf(Permissions.CanViewProducts),
    keywords: [],
    functionalities: [
      { label: "Crear inmueble", route: "/inventory/products/create", access: allOf(Permissions.CanCreateProducts), keywords: ["nuevo inmueble", "lote", "unidad", "producto"] },
      { label: "Crear inmuebles desde Excel", route: "/inventory/products", access: allOf(Permissions.CanCreateProducts), keywords: ["importar inmuebles", "carga masiva"] },
      { label: "Actualizar inmuebles desde Excel", route: "/inventory/products", access: anyOf(Permissions.CanCreateProducts, Permissions.CanEditProducts), keywords: ["actualizacion masiva"] },
      { label: "Exportar inmuebles a Excel", route: "/inventory/products", access: null, keywords: ["descargar inmuebles", "exportar"] },
    ],
  },
  {
    title: "Evaluaciones",
    route: "/crm/evaluations",
    icon: "heroicons_outline:shield-check",
    access: allOf(Permissions.CanViewEvaluations),
    keywords: [],
    functionalities: [
      { label: "Crear evaluación", route: "/crm/evaluations/create", access: allOf(Permissions.CanCreateEvaluations), keywords: ["debida diligencia", "kyc", "nueva evaluacion"] },
    ],
  },
  {
    title: "Pagos sin pagador identificado",
    route: "/crm/unidentified-payer-payments",
    icon: "heroicons_outline:banknotes",
    access: allOf(Permissions.CanViewCustomers, Permissions.CanViewEvaluations),
    keywords: [],
    functionalities: [
      { label: "Generar / importar Excel de pagos no identificados", route: "/crm/unidentified-payer-payments", access: null, keywords: ["pagos no identificados", "excel"] },
    ],
  },
  {
    title: "Pendientes de facturar",
    route: "/crm/pending-invoices",
    icon: "heroicons_outline:document-text",
    access: allOf(Permissions.CanSyncPaymentOrderInvoice),
    featureFlag: FeatureFlagEnum.ERP_INTEGRATION,
    keywords: [],
    functionalities: [
      { label: "Verificar facturas contra SAP", route: "/crm/pending-invoices", access: allOf(Permissions.CanSyncPaymentOrderInvoice), keywords: ["sincronizar", "sap", "facturacion"] },
    ],
  },
  {
    title: "Ingresos del mes",
    route: "/crm/payments-of-week",
    icon: "heroicons_outline:cash",
    access: allOf(Permissions.CanViewPaymentOrders),
    keywords: [],
    functionalities: [
      { label: "Imprimir recibo de pago", route: "/crm/payments-of-week", access: null, keywords: ["recibo", "comprobante", "ingresos"] },
    ],
  },
  {
    title: "Confirmación de pagos",
    route: "/crm/payments-request",
    icon: "heroicons_outline:wallet",
    access: allOf(Permissions.CanViewPaymentRequests),
    keywords: ["confirmar pago", "comprobantes"],
    functionalities: [],
  },
  {
    title: "Solicitudes de retiros",
    route: "/crm/withdrawal-requests",
    icon: "heroicons_outline:x-circle",
    access: allOf(Permissions.CanReviewPaymentOrderWithdrawals),
    keywords: ["retiros", "desistimiento"],
    functionalities: [],
  },
  {
    title: "Cambios de inmueble",
    route: "/crm/change-of-product",
    icon: "heroicons_outline:switch-horizontal",
    access: allOf(Permissions.CanViewChangeOfProducts),
    keywords: ["cambio de inmueble", "permuta"],
    functionalities: [],
  },
  {
    title: "Cesiones de derecho",
    route: "/crm/cession-of-right",
    icon: "heroicons_outline:list-bullet",
    access: allOf(Permissions.CanViewCessionOfRights),
    keywords: ["cesion", "traspaso"],
    functionalities: [],
  },
  {
    title: "Pago de comisiones",
    route: "/crm/commission-payments",
    icon: "heroicons_outline:document-check",
    access: allOf(Permissions.CanViewPaymentCommissions),
    keywords: ["pago comisiones"],
    functionalities: [],
  },
  {
    title: "Calendario de actividades",
    route: "/crm/activities",
    icon: "heroicons_outline:calendar",
    access: allOf(Permissions.CanViewAdvisorActivitiesReport),
    keywords: ["actividades", "calendario", "agenda"],
    functionalities: [],
  },
  {
    title: "Asesores de venta",
    route: "/crm/salesAdvisors",
    icon: "heroicons_outline:briefcase",
    access: allOf(Permissions.CanViewUsers),
    keywords: ["asesores", "vendedores"],
    functionalities: [],
  },
  {
    title: "Metas",
    route: "/globalConfig/goals/manage",
    icon: "heroicons_outline:chart-bar",
    access: anyOf(Permissions.CanViewGoal, Permissions.CanViewGoalMetric),
    keywords: [],
    functionalities: [
      { label: "Crear meta", route: "/globalConfig/goals/manage/new", access: allOf(Permissions.CanCreateGoal), keywords: ["nueva meta", "objetivo"] },
    ],
  },
  {
    title: "Dashboard",
    route: "/dashboard",
    icon: "heroicons_outline:chart-pie",
    access: null,
    keywords: ["kpi", "indicadores", "metricas"],
    functionalities: [],
  },
  configScreen("Proyectos", "/ecommerce/project", "heroicons_outline:office-building", Permissions.CanViewProjects, "Crear proyecto", Permissions.CanCreateProjects),
  configScreen("Datos de la empresa", "/ecommerce/businessData", "heroicons_outline:database", Permissions.CanViewCompanies, "Editar datos de la empresa", Permissions.CanEditCompanies),
  configScreen("Comisiones de proyecto", "/ecommerce/commissions", "heroicons_outline:wallet", Permissions.CanViewProjectCommissions, "Configurar comisión de proyecto", Permissions.CanCreateProjectCommissions),
  configScreen("Campañas publicitarias", "/crm/adCampaign", "heroicons_outline:megaphone", Permissions.CanSendAdCampaigns, "Enviar campaña", Permissions.CanSendAdCampaigns),
  configScreen("Bancos", "/globalConfig/banks", "mat_outline:comment_bank", Permissions.CanViewBanks, "Crear banco", Permissions.CanCreateBanks),
  configScreen("Cuentas de pago", "/globalConfig/paymentAccounts", "heroicons_outline:banknotes", Permissions.CanViewPaymentAccounts, "Crear cuenta de pago", Permissions.CanCreatePaymentAccounts),
  configScreen("Usuarios", "/globalConfig/users", "heroicons_outline:users", Permissions.CanViewUsers, "Crear usuario", Permissions.CanCreateUsers),
  configScreen("Roles", "/globalConfig/roles", "heroicons_outline:user-group", Permissions.CanViewRoles, "Asignar permisos a un rol", Permissions.CanEditRoles),
  configScreen("Notificaciones", "/globalConfig/notifications", "mat_outline:message", Permissions.CanViewNotifications, "Crear notificación", Permissions.CanCreateNotifications),
  configScreen("Tipos de origen", "/globalConfig/origin/typeOrigins", "mat_outline:account_tree", Permissions.CanViewTypeOrigins, "Crear tipo de origen", Permissions.CanCreateTypeOrigins),
  configScreen("Razones de pérdida", "/globalConfig/lossReasons", "heroicons_outline:trending-down", Permissions.CanViewLossReasons, "Crear razón de pérdida", Permissions.CanCreateLossReasons),
  configScreen("Categorías de razones de pérdida", "/globalConfig/lossReasonCategories", "heroicons_outline:tag", Permissions.CanViewLossReasons, "Crear categoría de razón de pérdida", Permissions.CanCreateLossReasons),
  configScreen("Razones de ganancia", "/globalConfig/winReasons", "heroicons_outline:trending-up", Permissions.CanViewWinReasons, "Crear razón de ganancia", Permissions.CanCreateWinReasons),
  configScreen("Tipos de descuento", "/globalConfig/discountTypes", "heroicons_outline:tag", Permissions.CanViewDiscountTypes, "Crear tipo de descuento", Permissions.CanCreateDiscountTypes),
  {
    title: "Reportes",
    route: "/crm/report",
    icon: "heroicons_outline:folder-open",
    access: anyOf(...REPORTS.map((report) => report.permission)),
    keywords: REPORT_KEYWORDS,
    functionalities: REPORTS.map((report) => ({
      label: report.label,
      route: report.route,
      access: allOf(report.permission),
      keywords: REPORT_KEYWORDS,
    })),
  },
];
